/**
 * Ted #46 (#1218) — edit the run card before printing.
 *
 * Ted, 10-09 call: "edit the run card before printing (add a field or a
 * note)". This is the step behind "Edit card" on the check-in print prompt:
 * the desk adds a note or a labelled field to THIS dog's card for THIS
 * stay, or corrects allergies / feeding / meds / behavior for the stay,
 * then goes back and prints.
 *
 * Johnny's decisions, 2026-10-09 (final), and where each lives:
 *   · Edits are saved ON THE STAY (booking + dog) — the server's
 *     `run-card-edits` PUT; a reprint keeps them.
 *   · Allergy / feeding / meds / behavior edits offer "Also save to <dog>'s
 *     profile", OPT-IN per edit: the box is unticked by default and only
 *     enabled once that field actually differs from the profile. Notes and
 *     extra fields are stay-only and offer no such box.
 *
 * ── DESIGN-PHILOSOPHY ───────────────────────────────────────────────────
 * · Counter Test — it opens pre-filled with what the card will print, so a
 *   one-line note is: type, Save. Back is always one tap and loses nothing
 *   the server has.
 * · P2 — exactly one filled-primary button (Save to card).
 * · P5 (never lie about data) — a field that differs from the profile says
 *   what the profile holds, so "this stay" and "on file" are never confused.
 * · P7 — every control is ≥44px; inputs are full-width for a thumb.
 * · Five states: loading (skeleton), empty (a dog with nothing on file gets
 *   teaching placeholders), default, error (load: retry, or print without
 *   edits; save: what happened, nothing lost, try again), saving.
 */
import { useCallback, useEffect, useId, useState } from 'react';
import {
  fetchRunCardEdits,
  humanizeApiError,
  saveRunCardEdits,
  type RunCardEditsInput,
  type RunCardEditsView,
  type RunCardProfileField,
} from '../../lib/api.js';
import './RunCardEditor.css';
// Sawyer 06 beds-lockers-runcard (B4) — the stay's locker, at the top of the editor, saved first.
import { LOCKER_FIELD_HINT, StayLockerSection, useStayLocker } from './StayLockerSection.js';

export const MAX_EXTRA_FIELDS = 8;

interface ExtraFieldDraft {
  readonly key: number;
  readonly label: string;
  readonly value: string;
}

interface CareDraft {
  readonly value: string;
  readonly saveToProfile: boolean;
}

type CareDrafts = Readonly<Record<RunCardProfileField, CareDraft>>;

interface Drafts {
  readonly note: string;
  readonly fields: ReadonlyArray<ExtraFieldDraft>;
  readonly care: CareDrafts;
}

type Phase =
  | { readonly kind: 'loading' }
  | { readonly kind: 'load_error'; readonly message: string }
  | { readonly kind: 'ready' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'save_error'; readonly message: string };

const CARE_FIELDS: ReadonlyArray<{
  readonly key: RunCardProfileField;
  readonly label: string;
  readonly placeholder: string;
  readonly rows: number;
}> = [
  { key: 'allergies', label: 'Allergies', placeholder: 'Chicken, beef', rows: 2 },
  { key: 'feeding', label: 'Feeding', placeholder: 'Half a cup, 7am and 5pm', rows: 2 },
  { key: 'medications', label: 'Medications', placeholder: 'Apoquel, one tablet with breakfast', rows: 2 },
  { key: 'behavior', label: 'Behavior', placeholder: 'Shy with big dogs; no group play', rows: 2 },
];

const norm = (v: string | null | undefined): string => (v ?? '').trim();

/** What the editor shows for a care field: this stay's value, else the profile's. */
function seedOf(view: RunCardEditsView, field: RunCardProfileField): string {
  return view.stay[field] ?? view.profile[field] ?? '';
}

function draftsFrom(view: RunCardEditsView): Drafts {
  const care = {} as Record<RunCardProfileField, CareDraft>;
  for (const f of CARE_FIELDS) {
    care[f.key] = { value: seedOf(view, f.key), saveToProfile: false };
  }
  return {
    note: view.note ?? '',
    fields: view.fields.map((f, i) => ({ key: i, label: f.label, value: f.value })),
    care,
  };
}

/**
 * The PUT body: only what changed since the editor opened. A care value set
 * back to exactly what the profile says is sent as null ("follow the
 * profile"), so the card does not tag it "(this stay)".
 */
export function buildEditsPayload(view: RunCardEditsView, drafts: Drafts): RunCardEditsInput {
  const out: RunCardEditsInput = {};
  if (norm(drafts.note) !== norm(view.note)) out.note = drafts.note;
  const fields = drafts.fields
    .map((f) => ({ label: f.label.trim(), value: f.value.trim() }))
    .filter((f) => f.label.length > 0);
  if (JSON.stringify(fields) !== JSON.stringify(view.fields)) out.fields = fields;
  const save: Partial<Record<RunCardProfileField, boolean>> = {};
  for (const { key } of CARE_FIELDS) {
    const d = drafts.care[key];
    const value = norm(d.value);
    const differsFromProfile = value !== norm(view.profile[key]);
    if (d.saveToProfile && differsFromProfile) {
      out[key] = value;
      save[key] = true;
      continue;
    }
    if (value === norm(seedOf(view, key))) continue; // untouched
    out[key] = differsFromProfile ? value : null;
  }
  if (Object.keys(save).length > 0) out.saveToProfile = save;
  return out;
}

interface RunCardEditorProps {
  readonly apiUrl: string;
  readonly token: string;
  readonly bookingId: string;
  readonly dogId: string;
  readonly dogName: string;
  /** Called after a successful save; resolves once the card has reloaded. */
  readonly onSaved: () => Promise<void>;
  readonly onBack: () => void;
  /** Sawyer 06 beds-lockers-runcard (B4) — the night printed (YYYY-MM-DD); absent = the facility's today. */
  readonly night?: string;
}

export function RunCardEditor({
  apiUrl,
  token,
  bookingId,
  dogId,
  dogName,
  onSaved,
  onBack,
  night,
}: RunCardEditorProps): JSX.Element {
  const idBase = useId();
  // Sawyer 06 beds-lockers-runcard (B4)
  const locker = useStayLocker({ apiUrl, token, bookingId, dogId, night });
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [view, setView] = useState<RunCardEditsView | null>(null);
  const [drafts, setDrafts] = useState<Drafts | null>(null);
  const [nextKey, setNextKey] = useState(1000);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase({ kind: 'loading' });
    void (async () => {
      try {
        const res = await fetchRunCardEdits(apiUrl, token, bookingId, dogId);
        if (cancelled) return;
        setView(res.run_card_edits);
        setDrafts(draftsFrom(res.run_card_edits));
        setPhase({ kind: 'ready' });
      } catch (err) {
        if (cancelled) return;
        setPhase({
          kind: 'load_error',
          message: humanizeApiError(err instanceof Error ? err.message : 'unknown'),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, token, bookingId, dogId, reloadKey]);

  const setCare = useCallback(
    (field: RunCardProfileField, patch: Partial<CareDraft>) =>
      setDrafts((d) =>
        d ? { ...d, care: { ...d.care, [field]: { ...d.care[field], ...patch } } } : d,
      ),
    [],
  );

  const save = useCallback(async () => {
    if (!view || !drafts) return;
    setPhase({ kind: 'saving' });
    // Sawyer 06 beds-lockers-runcard (B4) — the locker first: if it fails,
    // nothing else is sent and its sentence shows under the field.
    if (!(await locker.saveIfChanged())) {
      setPhase({ kind: 'ready' });
      return;
    }
    try {
      const payload = buildEditsPayload(view, drafts);
      if (Object.keys(payload).length > 0) {
        await saveRunCardEdits(apiUrl, token, bookingId, dogId, payload);
      }
      await onSaved();
    } catch (err) {
      setPhase({
        kind: 'save_error',
        message: humanizeApiError(err instanceof Error ? err.message : 'unknown'),
      });
    }
  }, [apiUrl, token, bookingId, dogId, view, drafts, onSaved, locker]);

  const busy = phase.kind === 'saving';

  if (phase.kind === 'loading') {
    return (
      <div className="ferro-run-card-editor" data-testid="run-card-editor">
        <EditorHeader dogName={dogName} />
        <div className="ferro-run-card-editor__skeleton" aria-busy="true" data-testid="run-card-editor-loading">
          <span className="ferro-run-card-editor__skeleton-line" />
          <span className="ferro-run-card-editor__skeleton-line" />
          <span className="ferro-run-card-editor__skeleton-line ferro-run-card-editor__skeleton-line--short" />
        </div>
      </div>
    );
  }

  if (phase.kind === 'load_error' || !view || !drafts) {
    const message = phase.kind === 'load_error' ? phase.message : 'The card could not be loaded.';
    return (
      <div className="ferro-run-card-editor" data-testid="run-card-editor">
        <EditorHeader dogName={dogName} />
        <div className="ferro-run-card-editor__error" role="alert" data-testid="run-card-editor-load-error">
          <p className="ferro-run-card-editor__error-what">{message}</p>
          <p className="ferro-run-card-editor__error-how">
            {dogName} is still checked in. Try again, or go back and print the card without edits.
          </p>
        </div>
        <div className="ferro-blocking-dialog__actions">
          <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onBack} data-testid="run-card-editor-back">
            Back
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => setReloadKey((k) => k + 1)}
            data-testid="run-card-editor-retry"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="ferro-run-card-editor"
      data-testid="run-card-editor"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <EditorHeader dogName={dogName} />

      {/* Sawyer 06 beds-lockers-runcard (B4) */}
      <StayLockerSection locker={locker} dogName={dogName} disabled={busy} />

      <fieldset className="ferro-run-card-editor__group" disabled={busy}>
        <legend className="ferro-run-card-editor__legend">This stay only</legend>
        <label className="ferro-run-card-editor__label" htmlFor={`${idBase}-note`}>
          Note
        </label>
        <textarea
          id={`${idBase}-note`}
          className="ferro-run-card-editor__input"
          rows={2}
          value={drafts.note}
          placeholder="Owner reachable after 5pm; brought her own bowl"
          onChange={(e) => setDrafts({ ...drafts, note: e.target.value })}
          data-testid="run-card-editor-note"
        />

        {drafts.fields.map((f, i) => (
          <div className="ferro-run-card-editor__field-row" key={f.key} data-testid={`run-card-editor-field-${i}`}>
            <input
              className="ferro-run-card-editor__input ferro-run-card-editor__field-label"
              aria-label={`Field ${i + 1} name`}
              placeholder="Field, e.g. Pickup by"
              maxLength={40}
              value={f.label}
              onChange={(e) =>
                setDrafts({
                  ...drafts,
                  fields: drafts.fields.map((x) => (x.key === f.key ? { ...x, label: e.target.value } : x)),
                })
              }
              data-testid={`run-card-editor-field-label-${i}`}
            />
            <input
              className="ferro-run-card-editor__input"
              aria-label={`Field ${i + 1} value`}
              placeholder="Value"
              maxLength={200}
              value={f.value}
              onChange={(e) =>
                setDrafts({
                  ...drafts,
                  fields: drafts.fields.map((x) => (x.key === f.key ? { ...x, value: e.target.value } : x)),
                })
              }
              data-testid={`run-card-editor-field-value-${i}`}
            />
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost ferro-run-card-editor__remove"
              aria-label={`Remove field ${f.label || i + 1}`}
              onClick={() => setDrafts({ ...drafts, fields: drafts.fields.filter((x) => x.key !== f.key) })}
            >
              Remove
            </button>
          </div>
        ))}
        {/* Sawyer 06 beds-lockers-runcard (B4) — a free-text "Locker" field would print at the bottom, not in the header box. */}
        {locker.state.status === 'ready' && drafts.fields.some((f) => /^locker$/i.test(f.label.trim())) ? (
          <p className="ferro-run-card-editor__hint" data-testid="run-card-editor-locker-field-hint">
            {LOCKER_FIELD_HINT}
          </p>
        ) : null}
        {drafts.fields.length < MAX_EXTRA_FIELDS ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-run-card-editor__add"
            onClick={() => {
              setDrafts({ ...drafts, fields: [...drafts.fields, { key: nextKey, label: '', value: '' }] });
              setNextKey((k) => k + 1);
            }}
            data-testid="run-card-editor-add-field"
          >
            + Add a field
          </button>
        ) : null}
      </fieldset>

      <fieldset className="ferro-run-card-editor__group" disabled={busy}>
        <legend className="ferro-run-card-editor__legend">Care on the card</legend>
        {CARE_FIELDS.map((f) => {
          const d = drafts.care[f.key];
          const onFile = norm(view.profile[f.key]);
          const differs = norm(d.value) !== onFile;
          const inputId = `${idBase}-${f.key}`;
          return (
            <div className="ferro-run-card-editor__care" key={f.key} data-testid={`run-card-editor-care-${f.key}`}>
              <label className="ferro-run-card-editor__label" htmlFor={inputId}>
                {f.label}
              </label>
              <textarea
                id={inputId}
                className="ferro-run-card-editor__input"
                rows={f.rows}
                value={d.value}
                placeholder={f.placeholder}
                onChange={(e) => setCare(f.key, { value: e.target.value })}
                data-testid={`run-card-editor-${f.key}`}
              />
              {differs ? (
                <p className="ferro-run-card-editor__onfile" data-testid={`run-card-editor-onfile-${f.key}`}>
                  {onFile ? `On ${dogName}’s profile: ${onFile}` : `Nothing on ${dogName}’s profile`}
                  {/* A stay-only blank cannot hide what the profile says
                      (the server's override rule) — say so before Save. */}
                  {onFile && norm(d.value) === '' && !d.saveToProfile
                    ? '. Left blank, the card still prints that; tick the box to remove it from the profile.'
                    : ''}
                </p>
              ) : null}
              <label className="ferro-run-card-editor__save">
                <input
                  type="checkbox"
                  checked={differs && d.saveToProfile}
                  disabled={!differs}
                  onChange={(e) => setCare(f.key, { saveToProfile: e.target.checked })}
                  data-testid={`run-card-editor-save-${f.key}`}
                />
                <span>Also save to {dogName}’s profile</span>
              </label>
            </div>
          );
        })}
        <p className="ferro-run-card-editor__hint">
          Without the profile box, a change prints on this stay’s card only.
        </p>
      </fieldset>

      {phase.kind === 'save_error' ? (
        <div className="ferro-run-card-editor__error" role="alert" data-testid="run-card-editor-save-error">
          <p className="ferro-run-card-editor__error-what">{phase.message}</p>
          <p className="ferro-run-card-editor__error-how">Your changes are still here. Try saving again.</p>
        </div>
      ) : null}

      <div className="ferro-blocking-dialog__actions">
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={onBack}
          disabled={busy}
          data-testid="run-card-editor-back"
        >
          Back
        </button>
        <button type="submit" className="ferro-btn ferro-btn--primary" disabled={busy} data-testid="run-card-editor-save">
          {busy ? 'Saving…' : 'Save to card'}
        </button>
      </div>
    </form>
  );
}

function EditorHeader({ dogName }: { readonly dogName: string }): JSX.Element {
  return (
    <header className="ferro-run-card-editor__header">
      <h2 className="ferro-run-card-editor__title">Edit {dogName}’s run card</h2>
      <p className="ferro-run-card-editor__sub">Saved on this stay, so a reprint keeps it.</p>
    </header>
  );
}
